/** How long a lost WebGL context may stay lost, and how long rebuilding the scene after it returns may take, before the player is offered a reload. */
export const CONTEXT_RESTORE_WAIT_MS = 10_000;
export const CONTEXT_REBUILD_WAIT_MS = 90_000;

export interface ContextRecoveryView {
  /** Blocks the world behind a message while the GPU context is gone or being rebuilt. */
  show(message: string, progress?: number): void;
  hide(): void;
  /** Says the world could not be brought back and resolves when the player agrees to reload. */
  failed(message: string): Promise<void>;
  reload(): void;
}

/**
 * The page's side of a WebGL context loss (GPU reset, driver timeout, the browser reclaiming the oldest context): hold the world behind
 * a message while the scene rebuilds its GPU state, and offer a reload when the context does not come back or the rebuild stalls.
 * Rendering and the rebuild itself belong to `GameScene`; this only reports them. A new loss supersedes the one in progress.
 */
export class ContextRecovery {
  private run = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private view: ContextRecoveryView, private restoreWait = CONTEXT_RESTORE_WAIT_MS, private rebuildWait = CONTEXT_REBUILD_WAIT_MS) {}
  lost() {
    const run = ++this.run;
    this.view.show('The graphics were interrupted. Restoring…');
    this.expire(run, this.restoreWait, 'The graphics could not be restored.');
  }
  /** `rebuilt` settles when the scene draws again. */
  restored(rebuilt: Promise<void>) {
    const run = ++this.run;
    this.expire(run, this.rebuildWait, 'The graphics could not be rebuilt.');
    void rebuilt.then(() => { if (run === this.run) { this.clear(); this.view.hide(); } }, () => { if (run === this.run) this.fail('The graphics could not be rebuilt.'); });
  }
  progress(message: string, fraction: number) { if (this.timer) this.view.show(message, fraction); }
  private expire(run: number, wait: number, message: string) {
    this.clear();
    this.timer = setTimeout(() => { if (run === this.run) this.fail(message); }, wait);
  }
  private fail(message: string) {
    this.run++; this.clear();
    void this.view.failed(`${message} Reload to continue.`).then(() => this.view.reload());
  }
  private clear() { if (this.timer) clearTimeout(this.timer); this.timer = null; }
}
